import React, { useState, useEffect } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaTrash, FaTimes, FaSave } from "react-icons/fa";
import { FaPhoneAlt } from "react-icons/fa";
import { MdSms } from "react-icons/md";
import { LuMessageCircleWarning } from "react-icons/lu";
import { AxiosPostMethod } from "../helpers/customAxios";
import { useDispatch } from "react-redux";
import { SuccessToast } from "./toastify";
import { setShowPreloader } from "../store/activePage";
import HandleCancelCallService from "../hooks/cancelCallService";
import Swal from "sweetalert2";
import HandleCancelConfirmationSmsService from "../hooks/cancelConfirmationSms";
import formatCustomDateWithTime from "../helpers/customDateFormatWithTime";
import HandleCancelSmsService from "../hooks/cancelSmsService";
import PaymentDetailsPreview from "./payment-details-preview";
import DateTimeSegmentInput from "./date-time-segment-input";
import GetEventTypeList from "../hooks/getEventTypeList";

const EventDetailsModal = ({
  eventDetails,
  onClose,
  onSave,
  isEditable,
  refreshEvent,
}) => {
  const dispatch = useDispatch();
  const [eventTypes, setEventTypes] = useState([]);

  useEffect(() => {
    (async () => {
      const list = await GetEventTypeList();
      if (list && list.length > 0) {
        setEventTypes(list.map((i) => i.event_name));
      }
    })();
  }, []);

  const isStandard = eventDetails && eventTypes.includes(eventDetails.event_type);
  const initialValues = eventDetails ? {
    ...eventDetails,
    event_type: isStandard ? eventDetails.event_type : (eventDetails.event_type ? "Custom" : ""),
    custom_event_type: isStandard ? "" : (eventDetails.event_type || ""),
  } : {};

  const validationSchema = Yup.object({
    event_type: Yup.string().required("Event Type is required"),
    custom_event_type: Yup.string().when("event_type", {
      is: "Custom",
      then: (schema) => schema.trim().required("Custom event type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    event_date: Yup.date()
      .required("Event Date is required"),
    customer_name: Yup.string().required("Customer Name is required"),
    event_address: Yup.string().required("Event Address is required"),
    event_state: Yup.string().required("State is required"),
    event_city: Yup.string().required("City is required"),
    event_pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be 6 digits"),
  });

  const confirmCancel = (title, action) => {
    Swal.fire({
      title,
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "Keep it",
      confirmButtonColor: "#ef4444",
      background: "#ffffff",
      color: "#1f2937",
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(setShowPreloader(true));
        const res = await action();
        dispatch(setShowPreloader(false));
        if (res) {
          refreshEvent();
          onClose();
        }
      }
    });
  };

  const cancelSmsService = (eventId) =>
    confirmCancel("Cancel Reminder SMS service?", () =>
      HandleCancelSmsService({ event_id: eventId }).then((r) => {
        if (r) SuccessToast("Reminder SMS cancelled");
        return r;
      }),
    );

  const cancelCallService = (eventId) =>
    confirmCancel("Cancel Reminder Call service?", () =>
      HandleCancelCallService({ event_id: eventId }).then((r) => {
        if (r) SuccessToast("Reminder Call cancelled");
        return r;
      }),
    );

  const cancelConfirmationSms = (eventId) =>
    confirmCancel("Cancel Confirmation SMS service?", () =>
      HandleCancelConfirmationSmsService({ event_id: eventId }).then((r) => {
        if (r) SuccessToast("Confirmation SMS cancelled");
        return r;
      }),
    );

  const hasServices =
    eventDetails?.reminder_voice === 1 ||
    eventDetails?.reminder_sms === 1 ||
    eventDetails?.moi_confirmation_sms === 1;

  const FIELD_CLASS =
    "w-full px-3 py-2.5 rounded-xl text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent";
  
  const FIELD_STYLE = {
    background: "#ffffff",
    border: "1.5px solid #e5e7eb",
    color: "#111827",
  };
  
  const DISABLED_STYLE = {
    background: "#f9fafb",
    border: "1.5px solid #f3f4f6",
    color: "#6b7280",
    cursor: "default",
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-gray-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#ffffff",
          animation: "modalSlideUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
          maxHeight: "92vh",
        }}
        className="w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col shadow-2xl border border-gray-100"
      >
        <style>{`
          @keyframes modalSlideUp {
            from { opacity: 0; transform: translateY(24px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-gray-100 bg-gray-50/50"
        >
          <div>
            <p className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-0.5">
              {isEditable ? "Edit" : "View"}
            </p>
            <h2 className="text-xl font-black text-gray-900 tracking-tight">
              Event Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5 custom-modal-scroll bg-white">
          <style>{`
            .custom-modal-scroll::-webkit-scrollbar { width: 4px; }
            .custom-modal-scroll::-webkit-scrollbar-track { background: transparent; }
            .custom-modal-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 99px; }
          `}</style>

          {/* Form */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values) => {
              let submissionValues = { ...values };
              if (submissionValues.event_type === "Custom") {
                submissionValues.event_type = submissionValues.custom_event_type;
              }
              delete submissionValues.custom_event_type;
              onSave(submissionValues);
              onClose();
            }}
          >
            {({ values, setFieldValue, setFieldTouched }) => (
              <Form className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Event Type Dropdown / Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Event Type
                    </label>
                    {isEditable ? (
                      <>
                        <div className="relative">
                          <Field
                            as="select"
                            name="event_type"
                            className={FIELD_CLASS}
                            style={FIELD_STYLE}
                            onChange={(e) => {
                              setFieldValue("event_type", e.target.value);
                              if (e.target.value !== "Custom") {
                                setFieldValue("custom_event_type", "");
                              }
                            }}
                          >
                            <option value="">Select event type</option>
                            {eventTypes.map((type, i) => (
                              <option key={i} value={type}>
                                {type}
                              </option>
                            ))}
                            <option value="Custom">Custom</option>
                          </Field>
                        </div>
                        {values.event_type === "Custom" && (
                          <div className="mt-1.5">
                            <Field
                              type="text"
                              name="custom_event_type"
                              placeholder="Enter custom type"
                              className={FIELD_CLASS}
                              style={FIELD_STYLE}
                            />
                            <ErrorMessage
                              name="custom_event_type"
                              component="p"
                              className="text-xs font-semibold text-red-500 mt-1"
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <input
                        value={values.event_type === "Custom" ? values.custom_event_type : values.event_type}
                        disabled
                        readOnly
                        className={FIELD_CLASS}
                        style={DISABLED_STYLE}
                      />
                    )}
                    <ErrorMessage
                      name="event_type"
                      component="p"
                      className="text-xs font-semibold text-red-500"
                    />
                  </div>

                  {[
                    {
                      id: "customer_name",
                      label: "Customer Name",
                      type: "text",
                    },
                    {
                      id: "customer_mobile",
                      label: "Customer Mobile",
                      type: "text",
                    },
                    { id: "eventor_1", label: "Eventor 1", type: "text" },
                    { id: "eventor_2", label: "Eventor 2", type: "text" },
                    {
                      id: "event_address",
                      label: "Event Address",
                      type: "text",
                    },
                    { id: "event_state", label: "State", type: "text" },
                    { id: "event_city", label: "City", type: "text" },
                    { id: "event_pincode", label: "Pincode", type: "text" },
                  ].map(({ id, label, type }) => (
                    <div key={id} className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                        {label}
                      </label>
                      <Field
                        id={id}
                        name={id}
                        type={type}
                        disabled={!isEditable}
                        className={FIELD_CLASS}
                        style={isEditable ? FIELD_STYLE : DISABLED_STYLE}
                      />
                      <ErrorMessage
                        name={id}
                        component="p"
                        className="text-xs font-semibold text-red-500"
                      />
                    </div>
                  ))}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Event Date
                    </label>
                    {isEditable ? (
                      <DateTimeSegmentInput
                        name="event_date"
                        formikValue={values.event_date}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        accent="orange"
                      />
                    ) : (
                      <input
                        value={formatCustomDateWithTime(values.event_date)}
                        disabled
                        readOnly
                        className={FIELD_CLASS}
                        style={DISABLED_STYLE}
                      />
                    )}
                    <ErrorMessage
                      name="event_date"
                      component="p"
                      className="text-xs font-semibold text-red-500"
                    />
                  </div>
                </div>

                {isEditable && (
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      style={{
                        background: "linear-gradient(135deg, #ff8c00, #f97316)",
                        boxShadow: "0 4px 16px rgba(255,140,0,0.3)",
                      }}
                      className="flex items-center gap-2 px-6 py-2.5 text-white text-sm font-black rounded-xl hover:opacity-90 active:scale-95 transition-all"
                    >
                      <FaSave className="text-xs" />
                      Save Changes
                    </button>
                  </div>
                )}
              </Form>
            )}
          </Formik>

          <PaymentDetailsPreview eventDetails={eventDetails} />

          {/* Additional Services section */}
          {hasServices && (
            <div
              className="rounded-2xl overflow-hidden border border-orange-100 bg-orange-50/30"
            >
              {/* Services header */}
              <div
                className="px-4 py-2.5 flex items-center gap-2 border-b border-orange-100 bg-orange-50/50"
              >
                <span className="text-xs font-black text-orange-700 uppercase tracking-widest">
                  Additional Services
                </span>
              </div>

              <div className="px-4 py-3 space-y-2">
                {eventDetails?.reminder_voice === 1 && (
                  <ServiceRow
                    icon={<FaPhoneAlt />}
                    label="Reminder Call"
                    color="emerald"
                    date={formatCustomDateWithTime(
                      eventDetails?.reminder_call_date,
                    )}
                    onCancel={
                      isEditable
                        ? () => cancelCallService(eventDetails?.event_id)
                        : null
                    }
                  />
                )}
                {eventDetails?.reminder_sms === 1 && (
                  <ServiceRow
                    icon={<LuMessageCircleWarning />}
                    label="Reminder SMS"
                    color="amber"
                    date={formatCustomDateWithTime(
                      eventDetails?.reminder_sms_date,
                    )}
                    onCancel={
                      isEditable
                        ? () => cancelSmsService(eventDetails?.event_id)
                        : null
                    }
                  />
                )}
                {eventDetails?.moi_confirmation_sms === 1 && (
                  <ServiceRow
                    icon={<MdSms />}
                    label="Confirmation SMS"
                    color="blue"
                    date={null}
                    onCancel={
                      isEditable
                        ? () => cancelConfirmationSms(eventDetails?.event_id)
                        : null
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 shrink-0 flex justify-end border-t border-gray-100 bg-gray-50/50"
        >
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-gray-600 hover:text-gray-800 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceRow = ({ icon, label, color, date, onCancel }) => {
  const COLORS = {
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-100", icon: "text-emerald-500" },
    amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-100",   icon: "text-amber-500" },
    blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-100",    icon: "text-blue-500" },
  };
  const c = COLORS[color] || COLORS.blue;

  return (
    <div className={`flex items-center justify-between p-2.5 rounded-xl border ${c.bg} ${c.border}`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm ${c.icon}`}>
          {icon}
        </div>
        <div>
          <p className={`text-sm font-bold ${c.text}`}>{label}</p>
          {date && <p className="text-[10px] font-medium opacity-70 italic">Scheduled: {date}</p>}
        </div>
      </div>
      {onCancel && (
        <button
          onClick={onCancel}
          className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition"
          title="Cancel Service"
        >
          <FaTrash className="text-xs" />
        </button>
      )}
    </div>
  );
};

export default EventDetailsModal;
